import React, { useState, useEffect, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import {
  LayoutDashboard, BookOpen, Calculator, CalendarDays, Newspaper,
  PenSquare, Database, Plus, Trash2, Download, Upload, CheckCircle2,
  Circle, RotateCcw, Clock, Award, ChevronDown, ChevronUp, BookMarked,
  AlertTriangle, Target as TargetIcon, Save, Flame, TrendingUp,
  Activity, Calendar, BarChart3, Users, Crown, Medal,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

/* ---------------------------------------------------------------------- */
/*  PALETTE / TOKENS                                                       */
/* ---------------------------------------------------------------------- */
const C = {
  ink: "#1B2A4A",
  inkDeep: "#101B32",
  paper: "#F3EEE2",
  paperDeep: "#EAE2D0",
  card: "#FBF8F0",
  seal: "#A0312B",
  sealDeep: "#7E241F",
  gold: "#B8935F",
  goldLight: "#D9BE93",
  sage: "#5C7A5C",
  charcoal: "#2B2B26",
  faint: "#8A8370",
  line: "#D8CFB8",
  green: "#2BB8A3",
  darkGreen: "#1A7A6A",
  lightGreen: "#D4F3F0",
  orange: "#FF8A00",
  purple: "#6B46C1",
};

const serif = "'Source Serif 4', Georgia, serif";
const mono = "'IBM Plex Mono', 'Courier New', monospace";
const sans = "'Inter', system-ui, sans-serif";

/* ---------------------------------------------------------------------- */
/*  CONSTANTS / DEFAULTS                                                   */
/* ---------------------------------------------------------------------- */
const PRELIMS_DATE = new Date("2027-05-23T00:00:00");
const MAINS_DATE = new Date("2027-08-20T00:00:00");
const WEEKLY_TARGET_HOURS = 3 * 5 + 7 * 2; // 29

const STAGE_KEYS = ["read1", "rev1", "rev2", "rev3", "mcq", "pyq"];
const STAGE_LABELS = { read1: "Read 1", rev1: "Rev 1", rev2: "Rev 2", rev3: "Rev 3", mcq: "MCQs", pyq: "PYQs" };

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

const emptyStages = () => ({
  read1: { done: false, date: "" }, rev1: { done: false, date: "" },
  rev2: { done: false, date: "" }, rev3: { done: false, date: "" },
  mcq: { done: false, date: "" }, pyq: { done: false, date: "" },
});
const emptyDueDates = () => ({ read1: "", rev1: "", rev2: "", rev3: "", mcq: "", pyq: "" });

const GS_SUBJECT_NAMES = [
  "Modern History", "Polity", "Geography", "Economy", "Environment",
  "Ancient History", "Medieval History", "Art & Culture",
];

const OPTIONAL_TOPICS = {
  "Paper I": [
    "Linear Algebra", "Calculus", "Analytic Geometry",
    "Ordinary Differential Equations", "Dynamics & Statics", "Vector Analysis",
  ],
  "Paper II": [
    "Algebra", "Real Analysis", "Complex Analysis", "Linear Programming",
    "Partial Differential Equations", "Numerical Analysis & Computer Programming",
    "Mechanics & Fluid Dynamics",
  ],
};

function makeDefaultSubjects() {
  const gs = GS_SUBJECT_NAMES.map((name, i) => ({
    id: uid(), name, type: "GS", paper: null, order: i + 1,
    dueDates: emptyDueDates(), books: [], chapters: [],
  }));
  const optional = Object.entries(OPTIONAL_TOPICS).map(([paper, topics], i) => ({
    id: uid(), name: `Mathematics — ${paper}`, type: "Optional", paper, order: i + 1,
    dueDates: emptyDueDates(), books: [],
    chapters: topics.map((t, j) => ({ id: uid(), name: t, order: j + 1, stages: emptyStages() })),
  }));
  return [...gs, ...optional];
}

const CA_FOLDERS = [
  "Polity & Governance", "Economy", "Environment", "Science & Technology",
  "International Relations", "Social Issues & Schemes",
  "Geography & Disaster Management", "Security & Defence",
];

const EXTRA_LOG_CATEGORIES = [
  "Current Affairs", "Essay Writing", "Answer Writing Practice",
  "Mock Test Review", "General Revision",
];

const DEFAULT_STATE = {
  subjects: makeDefaultSubjects(),
  dailyLogs: [],
  currentAffairs: [],
  essays: [],
  mockTests: [],
};

// Google Apps Script web app URL
const SHEET_API_URL = "https://script.google.com/macros/s/AKfycbxjsky8VgQui2ZjZWXivyKaDV3-h3acdLvNKh5tyOpOG1xgGqIGWivf2byDELGt4ynceQ/exec";

async function loadFromSheet() {
  const res = await fetch(SHEET_API_URL, { method: "GET" });
  if (!res.ok) throw new Error(`Sheet load failed (${res.status})`);
  const json = await res.json();
  return json;
}

async function saveToSheet(state) {
  const res = await fetch(SHEET_API_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify(state),
  });
  if (!res.ok) throw new Error(`Sheet save failed (${res.status})`);
  return res.json();
}

/* ---------------------------------------------------------------------- */
/*  HELPERS                                                                */
/* ---------------------------------------------------------------------- */
function daysBetween(target) {
  const now = new Date();
  return Math.ceil((target - now) / 86400000);
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
function todayISO() { return new Date().toISOString().slice(0, 10); }
function startOfWeek(d = new Date()) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}
function chapterProgress(ch) {
  const done = STAGE_KEYS.filter((k) => ch.stages[k].done).length;
  return Math.round((done / STAGE_KEYS.length) * 100);
}
function subjectProgress(subj) {
  if (!subj.chapters.length) return 0;
  const total = subj.chapters.reduce((a, c) => a + chapterProgress(c), 0);
  return Math.round(total / subj.chapters.length);
}
function subjectStagePct(subj, key) {
  if (!subj.chapters.length) return 0;
  const done = subj.chapters.filter((c) => c.stages[key].done).length;
  return Math.round((done / subj.chapters.length) * 100);
}
function isOverdue(dueDate, pct) {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date(todayISO()) && pct < 100;
}
function subjectHours(subj, dailyLogs) {
  return dailyLogs.filter((l) => l.subjectId === subj.id).reduce((a, l) => a + (Number(l.hoursActual) || 0), 0);
}
function chapterHours(chapterId, dailyLogs) {
  return dailyLogs.filter((l) => l.chapterId === chapterId).reduce((a, l) => a + (Number(l.hoursActual) || 0), 0);
}
function getNextIncompleteStage(chapter) {
  const stages = chapter.stages;
  if (!stages.read1.done) return { key: 'read1', label: 'Read 1', index: 0 };
  if (!stages.rev1.done) return { key: 'rev1', label: 'Revision 1', index: 1 };
  if (!stages.rev2.done) return { key: 'rev2', label: 'Revision 2', index: 2 };
  if (!stages.rev3.done) return { key: 'rev3', label: 'Revision 3', index: 3 };
  if (!stages.mcq.done) return { key: 'mcq', label: 'MCQs', index: 4 };
  if (!stages.pyq.done) return { key: 'pyq', label: 'PYQs', index: 5 };
  return null; // All stages complete
}

/* ---------------------------------------------------------------------- */
/*  LEETCODE-STYLE CONTRIBUTION GRAPH - ENHANCED                          */
/* ---------------------------------------------------------------------- */

function ContributionGraph({ dailyLogs }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());

  const CELL = 11;
  const GAP = 3;
  const COL_W = CELL + GAP;

  const DARK = {
    bg: "#b7bfcb",
    card: "#161b22",
    empty: "#2d333b",
    l1: "#0e4429",
    l2: "#006d32",
    l3: "#26a641",
    l4: "#39d353",
    text: "#c9d1d9",
    faint: "#8b949e",
    border: "#30363d",
  };

  const studyData = useMemo(() => {
    const map = new Map();
    dailyLogs.forEach(log => {
      if (log.date) {
        const key = log.date;
        if (!map.has(key)) map.set(key, { date: key, hours: 0, mcqs: 0, entries: 0 });
        const entry = map.get(key);
        entry.hours += Number(log.hoursActual) || 0;
        entry.mcqs += Number(log.mcqsSolved) || 0;
        entry.entries += 1;
      }
    });
    return map;
  }, [dailyLogs]);

  const heatmapData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(today);
    start.setDate(start.getDate() - 364);
    start.setDate(start.getDate() - start.getDay());

    const weeks = [];
    const monthPositions = [];
    let lastMonth = -1;
    let current = new Date(start);

    while (current <= today) {
      const week = [];
      let monthLabel = null;
      let monthStartWeek = -1;
      let monthStartDay = -1;
      
      for (let i = 0; i < 7; i++) {
        const dateStr = current.toISOString().slice(0, 10);
        const data = studyData.get(dateStr) || { hours: 0, mcqs: 0, entries: 0 };
        const month = current.getMonth();
        const dayOfMonth = current.getDate();
        const inRange = current >= new Date(today.getFullYear(), today.getMonth() - 12, today.getDate()) && current <= today;

        if (lastMonth !== month && current <= today) {
          if (monthStartWeek === -1) {
            monthStartWeek = weeks.length;
            monthStartDay = i;
            monthLabel = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][month];
          }
          lastMonth = month;
        }

        week.push({
          date: dateStr,
          hours: data.hours,
          mcqs: data.mcqs,
          entries: data.entries,
          isToday: dateStr === todayISO(),
          inRange: current <= today,
          month: month,
          dayOfMonth: dayOfMonth,
          dayOfWeek: i,
        });
        current.setDate(current.getDate() + 1);
      }
      
      if (monthLabel && monthStartWeek >= 0) {
        monthPositions.push({
          month: monthLabel,
          weekIndex: monthStartWeek,
          dayIndex: monthStartDay,
        });
      }
      
      weeks.push(week);
    }
    return { weeks, monthPositions };
  }, [studyData]);

  const getColor = (hours, inRange) => {
    if (!inRange) return "transparent";
    if (hours === 0) return DARK.empty;
    if (hours < 1) return DARK.l1;
    if (hours < 2) return DARK.l2;
    if (hours < 4) return DARK.l3;
    return DARK.l4;
  };

  const stats = useMemo(() => {
    const flat = heatmapData.weeks.flat().filter(d => d.inRange);
    const submissions = flat.reduce((sum, d) => sum + d.entries, 0);
    const activeDays = flat.filter(d => d.hours > 0).length;

    let bestStreak = 0, currentStreak = 0;
    const sortedDates = Array.from(studyData.keys())
      .filter(d => studyData.get(d).hours > 0)
      .sort();
    for (let i = 0; i < sortedDates.length; i++) {
      const date = new Date(sortedDates[i]);
      const prevDate = i > 0 ? new Date(sortedDates[i - 1]) : null;
      currentStreak = prevDate && (date - prevDate) === 86400000 ? currentStreak + 1 : 1;
      bestStreak = Math.max(bestStreak, currentStreak);
    }

    let currentActiveStreak = 0;
    const checkDate = new Date();
    for (let i = 0; i < 365; i++) {
      const dateStr = checkDate.toISOString().slice(0, 10);
      const data = studyData.get(dateStr);
      if (data && data.hours > 0) {
        currentActiveStreak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else break;
    }

    return { submissions, activeDays, bestStreak, currentActiveStreak };
  }, [heatmapData, studyData]);

  const getMonthLabelPosition = (monthPos) => {
    const left = monthPos.weekIndex * COL_W + (monthPos.dayIndex * (CELL + GAP));
    return { left: `${left}px`, position: 'absolute', top: 0 };
  };

  return (
    <div
      className="w-full overflow-x-auto rounded-lg"
      style={{ background: DARK.card, padding: "16px 20px", border: `1px solid ${DARK.border}` }}
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: serif, fontSize: 20, fontWeight: 700, color: DARK.text }}>
            {stats.submissions}
          </span>
          <span style={{ fontFamily: sans, fontSize: 14, color: DARK.text }}>
            submissions in the past one year
          </span>
        </div>
        <div className="flex items-center gap-5">
          <span style={{ fontFamily: sans, fontSize: 13, color: DARK.faint }}>
            Total active days: <span style={{ color: DARK.text, fontWeight: 600 }}>{stats.activeDays}</span>
          </span>
          <span style={{ fontFamily: sans, fontSize: 13, color: DARK.faint }}>
            Max streak: <span style={{ color: DARK.text, fontWeight: 600 }}>{stats.bestStreak}</span>
          </span>
        </div>
      </div>

      <div className="relative" style={{ height: 18, marginBottom: 6 }}>
        {heatmapData.monthPositions.map((mp, idx) => {
          const position = getMonthLabelPosition(mp);
          
          let showLabel = true;
          if (idx < heatmapData.monthPositions.length - 1) {
            const nextPos = getMonthLabelPosition(heatmapData.monthPositions[idx + 1]);
            const currentLeft = parseInt(position.left);
            const nextLeft = parseInt(nextPos.left);
            if (nextLeft - currentLeft < 35) {
              showLabel = false;
            }
          }
          
          return showLabel ? (
            <div
              key={idx}
              className="absolute font-semibold"
              style={{ 
                left: position.left,
                top: 0,
                fontFamily: sans,
                fontSize: 11,
                color: DARK.faint,
                fontWeight: 600,
                letterSpacing: '0.3px',
                padding: '0 2px',
              }}
            >
              {mp.month}
            </div>
          ) : null;
        })}
      </div>

      <div className="flex" style={{ gap: GAP }}>
        <div className="flex flex-col" style={{ gap: GAP, marginRight: 4 }}>
          <div style={{ width: CELL, height: CELL, visibility: 'hidden' }} />
          <div style={{ width: CELL, height: CELL, fontFamily: sans, fontSize: 9, color: DARK.faint, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            Mon
          </div>
          <div style={{ width: CELL, height: CELL, visibility: 'hidden' }} />
          <div style={{ width: CELL, height: CELL, fontFamily: sans, fontSize: 9, color: DARK.faint, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            Wed
          </div>
          <div style={{ width: CELL, height: CELL, visibility: 'hidden' }} />
          <div style={{ width: CELL, height: CELL, fontFamily: sans, fontSize: 9, color: DARK.faint, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            Fri
          </div>
          <div style={{ width: CELL, height: CELL, visibility: 'hidden' }} />
        </div>
        
        {heatmapData.weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col" style={{ gap: GAP }}>
            {week.map((day, di) => (
              <div
                key={di}
                className="rounded-sm cursor-pointer relative"
                style={{
                  width: CELL,
                  height: CELL,
                  background: getColor(day.hours, day.inRange),
                  visibility: day.inRange ? "visible" : "hidden",
                  outline: day.isToday ? `1.5px solid ${DARK.l4}` : "none",
                  outlineOffset: 1,
                  position: 'relative',
                }}
                onMouseEnter={() => day.inRange && setSelectedDate(day)}
                onMouseLeave={() => setSelectedDate(null)}
              >
                {day.dayOfMonth === 1 && day.inRange && (
                  <span 
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ 
                      color: day.hours > 0 ? DARK.text : DARK.faint,
                      fontSize: 6,
                      fontWeight: 700,
                      opacity: 0.7,
                      pointerEvents: 'none',
                      textShadow: '0 0 2px rgba(0,0,0,0.5)'
                    }}
                  >
                    {day.dayOfMonth}
                  </span>
                )}
                
                {selectedDate?.date === day.date && (
                  <div
                    className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-md whitespace-nowrap z-10 shadow-lg"
                    style={{ background: "#010409", color: DARK.text, fontFamily: sans, fontSize: 11, border: `1px solid ${DARK.border}` }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{fmtDate(day.date)}</div>
                    <div style={{ color: DARK.l4 }}>{day.hours.toFixed(1)} hours • {day.mcqs} MCQs</div>
                    {day.entries > 0 && (
                      <div style={{ color: DARK.faint, fontSize: 10 }}>{day.entries} entry{day.entries > 1 ? 's' : ''}</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end gap-1 mt-3">
        <span style={{ fontFamily: sans, fontSize: 11, color: DARK.faint, marginRight: 4 }}>Less</span>
        {[DARK.empty, DARK.l1, DARK.l2, DARK.l3, DARK.l4].map((c, i) => (
          <div key={i} className="rounded-sm" style={{ width: CELL, height: CELL, background: c }} />
        ))}
        <span style={{ fontFamily: sans, fontSize: 11, color: DARK.faint, marginLeft: 4 }}>More</span>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/*  LEETCODE-STYLE DASHBOARD                                             */
/* ---------------------------------------------------------------------- */
function LeetCodeDashboard({ state }) {
  const prelimsDays = daysBetween(new Date(PRELIMS_DATE));
  const mainsDays = daysBetween(new Date(MAINS_DATE));
  const gsSubjects = state.subjects.filter((s) => s.type === "GS");
  const optSubjects = state.subjects.filter((s) => s.type === "Optional");

  const overallGS = useMemo(() => gsSubjects.length ? Math.round(gsSubjects.reduce((a, s) => a + subjectProgress(s), 0) / gsSubjects.length) : 0, [state.subjects]);
  const overallOptional = useMemo(() => optSubjects.length ? Math.round(optSubjects.reduce((a, s) => a + subjectProgress(s), 0) / optSubjects.length) : 0, [state.subjects]);

  const weekStart = startOfWeek();
  const weekHours = useMemo(() => state.dailyLogs.filter((l) => new Date(l.date) >= weekStart).reduce((a, l) => a + (Number(l.hoursActual) || 0), 0), [state.dailyLogs]);
  const totalHours = useMemo(() => state.dailyLogs.reduce((a, l) => a + (Number(l.hoursActual) || 0), 0), [state.dailyLogs]);
  const totalMcqs = useMemo(() => state.dailyLogs.reduce((a, l) => a + (Number(l.mcqsSolved) || 0), 0), [state.dailyLogs]);
  const totalChapters = state.subjects.reduce((a, s) => a + s.chapters.length, 0);
  const totalBooks = state.subjects.reduce((a, s) => a + s.books.length, 0);
  const totalEssays = state.essays.length;
  const totalMocks = state.mockTests.length;

  const dailyChartData = useMemo(() => {
    const last30Days = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().slice(0, 10);
      const hours = state.dailyLogs
        .filter(l => l.date === dateStr)
        .reduce((sum, l) => sum + (Number(l.hoursActual) || 0), 0);
      last30Days.push({
        date: dateStr,
        hours: Math.round(hours * 10) / 10,
        label: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
      });
    }
    return last30Days;
  }, [state.dailyLogs]);

  const subjectHoursData = useMemo(() => {
    return state.subjects.map(s => ({
      name: s.name.length > 20 ? s.name.slice(0, 18) + '…' : s.name,
      hours: subjectHours(s, state.dailyLogs)
    })).filter(d => d.hours > 0).sort((a, b) => b.hours - a.hours);
  }, [state.subjects, state.dailyLogs]);

  const completionData = useMemo(() => {
    const gsChapters = state.subjects.filter(s => s.type === "GS").flatMap(s => s.chapters);
    const optChapters = state.subjects.filter(s => s.type === "Optional").flatMap(s => s.chapters);
    
    const calcAvg = (chapters) => {
      if (!chapters.length) return 0;
      const total = chapters.reduce((sum, c) => sum + chapterProgress(c), 0);
      return Math.round(total / chapters.length);
    };
    
    return {
      gs: calcAvg(gsChapters),
      optional: calcAvg(optChapters),
      overall: overallGS && overallOptional ? Math.round((overallGS + overallOptional) / 2) : 0
    };
  }, [state.subjects, overallGS, overallOptional]);

  const badges = useMemo(() => {
    const list = [];
    if (totalHours > 100) list.push({ icon: <Crown size={16} />, label: '100+ Hours' });
    if (totalMcqs > 500) list.push({ icon: <Medal size={16} />, label: '500+ MCQs' });
    if (totalEssays > 10) list.push({ icon: <BookOpen size={16} />, label: '10+ Essays' });
    if (totalMocks > 5) list.push({ icon: <TargetIcon size={16} />, label: '5+ Mocks' });
    if (completionData.overall > 50) list.push({ icon: <TrendingUp size={16} />, label: '50% Complete' });
    if (list.length === 0) list.push({ icon: <Activity size={16} />, label: 'Starting Journey' });
    return list;
  }, [totalHours, totalMcqs, totalEssays, totalMocks, completionData.overall]);

  const overdueList = useMemo(() => {
    const rows = [];
    state.subjects.forEach((s) => {
      STAGE_KEYS.forEach((k) => {
        const due = s.dueDates[k];
        const pct = subjectStagePct(s, k);
        if (due && new Date(due) >= new Date(todayISO()) && new Date(due) - new Date(todayISO()) <= 14 * 86400000 && pct < 100) {
          rows.push({ subject: s.name, stage: STAGE_LABELS[k], due, pct, overdue: false });
        } else if (isOverdue(due, pct)) {
          rows.push({ subject: s.name, stage: STAGE_LABELS[k], due, pct, overdue: true });
        }
      });
    });
    return rows.sort((a, b) => (b.overdue - a.overdue) || (new Date(a.due) - new Date(b.due))).slice(0, 5);
  }, [state.subjects]);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-lg p-4 text-center" style={{ background: C.ink }}>
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: C.gold, textTransform: "uppercase" }}>
            Prelims 2027
          </div>
          <div style={{ fontFamily: serif, fontSize: 32, fontWeight: 700, color: C.paper }}>
            {prelimsDays > 0 ? prelimsDays : "—"}
          </div>
          <div style={{ fontFamily: mono, fontSize: 10, color: C.goldLight }}>days left</div>
        </div>
        <div className="rounded-lg p-4 text-center" style={{ background: C.inkDeep }}>
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: C.gold, textTransform: "uppercase" }}>
            Mains 2027
          </div>
          <div style={{ fontFamily: serif, fontSize: 32, fontWeight: 700, color: C.paper }}>
            {mainsDays > 0 ? mainsDays : "—"}
          </div>
          <div style={{ fontFamily: mono, fontSize: 10, color: C.goldLight }}>days left</div>
        </div>
        <div className="rounded-lg p-4" style={{ background: C.paperDeep }}>
          <div style={{ fontFamily: mono, fontSize: 10, color: C.faint, textTransform: "uppercase" }}>
            <Flame size={14} className="inline mr-1" /> Streak
          </div>
          <div style={{ fontFamily: serif, fontSize: 28, fontWeight: 700, color: C.ink }}>
            {totalHours.toFixed(1)} total hours
          </div>
        </div>
        <div className="rounded-lg p-4" style={{ background: C.paperDeep }}>
          <div style={{ fontFamily: mono, fontSize: 10, color: C.faint, textTransform: "uppercase" }}>
            <Activity size={14} className="inline mr-1" /> Progress
          </div>
          <div style={{ fontFamily: serif, fontSize: 28, fontWeight: 700, color: C.green }}>
            {completionData.overall}%
          </div>
          <div style={{ fontFamily: mono, fontSize: 10, color: C.faint }}>GS {completionData.gs}% • Optional {completionData.optional}%</div>
        </div>
      </div>

      {overdueList.length > 0 && (
        <div className="rounded-lg p-4" style={{ background: C.card, border: `1px solid ${C.line}` }}>
          <span style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.ink, display: 'block', marginBottom: 12 }}>
            <AlertTriangle size={16} className="inline mr-2" /> Upcoming Deadlines
          </span>
          <div className="flex flex-col gap-2">
            {overdueList.map((r, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5 px-2 rounded" style={{ background: r.overdue ? '#FDF0EE' : '#F8F5EE' }}>
                {r.overdue ? <AlertTriangle size={14} color={C.seal} /> : <TargetIcon size={14} color={C.gold} />}
                <span style={{ fontFamily: sans, fontSize: 12, color: C.charcoal, flex: 1 }}>{r.subject}</span>
                <span style={{ fontFamily: mono, fontSize: 10.5, color: C.faint }}>{r.stage}</span>
                <span style={{ fontFamily: mono, fontSize: 10.5, color: r.overdue ? C.seal : C.sage }}>{fmtDate(r.due)}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: C.faint }}>{r.pct}% done</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg p-4" style={{ background: C.card, border: `1px solid ${C.line}` }}>
        <div className="flex items-center justify-between mb-4">
          <span style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, color: C.ink }}>
            Study Activity
          </span>
          <span style={{ fontFamily: mono, fontSize: 10, color: C.faint }}>
            {state.dailyLogs.length} entries logged
          </span>
        </div>
        <ContributionGraph dailyLogs={state.dailyLogs} />
      </div>

      <div className="rounded-lg p-4" style={{ background: C.card, border: `1px solid ${C.line}` }}>
        <div className="flex items-center justify-between mb-2">
          <span style={{ fontFamily: mono, fontSize: 11, color: C.faint, textTransform: "uppercase" }}>
            <Calendar size={14} className="inline mr-2" /> This Week's Discipline
          </span>
          <span style={{ fontFamily: mono, fontSize: 12, color: C.gold }}>
            {weekHours.toFixed(1)} / {WEEKLY_TARGET_HOURS} hrs
          </span>
        </div>
        <ProgressBar pct={Math.min(100, (weekHours / WEEKLY_TARGET_HOURS) * 100)} color={C.gold} />
        <div className="flex items-center justify-between mt-2">
          <span style={{ fontFamily: sans, fontSize: 12, color: C.faint }}>{weekHours >= WEEKLY_TARGET_HOURS ? '✅ On track!' : 'Keep pushing!'}</span>
          <span style={{ fontFamily: mono, fontSize: 10, color: C.faint }}>{weekHours >= WEEKLY_TARGET_HOURS ? '100%' : Math.round((weekHours / WEEKLY_TARGET_HOURS) * 100) + '%'}</span>
        </div>
      </div>

      <div className="rounded-lg p-4" style={{ background: C.card, border: `1px solid ${C.line}` }}>
        <div className="flex items-center justify-between mb-4">
          <span style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, color: C.ink }}>
            <BarChart3 size={18} className="inline mr-2" /> Last 30 Days
          </span>
          <span style={{ fontFamily: mono, fontSize: 10, color: C.faint }}>Daily study hours</span>
        </div>
        <div style={{ height: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyChartData}>
              <CartesianGrid stroke={C.line} strokeDasharray="3 3" />
              <XAxis 
                dataKey="label" 
                tick={{ fontFamily: mono, fontSize: 9, fill: C.faint }}
                interval={6}
              />
              <YAxis 
                tick={{ fontFamily: mono, fontSize: 9, fill: C.faint }}
                domain={[0, 'auto']}
              />
              <Tooltip 
                contentStyle={{ 
                  fontFamily: sans, 
                  fontSize: 12, 
                  borderColor: C.line,
                  background: C.card
                }}
                formatter={(value) => [`${value} hours`, 'Study Time']}
              />
              <Line 
                type="monotone" 
                dataKey="hours" 
                stroke={C.gold} 
                strokeWidth={2}
                dot={{ fill: C.gold, r: 3 }}
                name="Hours"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg p-4" style={{ background: C.card, border: `1px solid ${C.line}` }}>
          <span style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.ink, display: 'block', marginBottom: 12 }}>
            <BookOpen size={16} className="inline mr-2" /> Subject-wise Hours
          </span>
          <div className="flex flex-col gap-2 max-h-[220px] overflow-y-auto pr-1">
            {subjectHoursData.slice(0, 8).map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <span style={{ fontFamily: sans, fontSize: 12, width: 140, color: C.charcoal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.name}
                </span>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: C.paperDeep }}>
                  <div 
                    className="h-full rounded-full"
                    style={{ 
                      width: `${Math.min(100, (s.hours / Math.max(1, subjectHoursData[0]?.hours || 1)) * 100)}%`,
                      background: i < 3 ? C.gold : C.sage
                    }}
                  />
                </div>
                <span style={{ fontFamily: mono, fontSize: 11, color: C.faint, width: 50, textAlign: 'right' }}>
                  {s.hours.toFixed(1)}h
                </span>
              </div>
            ))}
            {subjectHoursData.length === 0 && (
              <span style={{ fontFamily: sans, fontSize: 12, color: C.faint }}>No study hours logged yet.</span>
            )}
          </div>
        </div>

        <div className="rounded-lg p-4" style={{ background: C.card, border: `1px solid ${C.line}` }}>
          <span style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.ink, display: 'block', marginBottom: 12 }}>
            <Award size={16} className="inline mr-2" /> Badges & Achievements
          </span>
          <div className="flex flex-wrap gap-2 mb-4">
            {badges.map((badge, i) => (
              <div 
                key={i}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full"
                style={{ 
                  background: C.paperDeep,
                  border: `1px solid ${C.line}`,
                  fontFamily: mono,
                  fontSize: 10,
                  color: C.ink
                }}
              >
                {badge.icon}
                {badge.label}
              </div>
            ))}
          </div>
          
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-sm" style={{ color: C.charcoal }}>
              <span>MCQs Solved</span>
              <span style={{ fontFamily: mono, color: C.gold }}>{totalMcqs}</span>
            </div>
            <div className="flex items-center justify-between text-sm" style={{ color: C.charcoal }}>
              <span>Chapters Tracked</span>
              <span style={{ fontFamily: mono, color: C.gold }}>{totalChapters}</span>
            </div>
            <div className="flex items-center justify-between text-sm" style={{ color: C.charcoal }}>
              <span>Essays Written</span>
              <span style={{ fontFamily: mono, color: C.gold }}>{totalEssays}</span>
            </div>
            <div className="flex items-center justify-between text-sm" style={{ color: C.charcoal }}>
              <span>Mock Tests</span>
              <span style={{ fontFamily: mono, color: C.gold }}>{totalMocks}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg p-4" style={{ background: C.card, border: `1px solid ${C.line}` }}>
        <span style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.ink, display: 'block', marginBottom: 12 }}>
          <TrendingUp size={16} className="inline mr-2" /> Subject Completion
        </span>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {state.subjects.slice(0, 8).map((s) => (
            <div key={s.id} className="text-center p-2 rounded" style={{ background: C.paperDeep }}>
              <div style={{ fontFamily: sans, fontSize: 11, color: C.charcoal, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {s.name.length > 15 ? s.name.slice(0, 14) + '…' : s.name}
              </div>
              <div style={{ fontFamily: serif, fontSize: 20, fontWeight: 700, color: subjectProgress(s) > 50 ? C.green : C.ink }}>
                {subjectProgress(s)}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/*  SHARED UI ATOMS                                                        */
/* ---------------------------------------------------------------------- */
function Seal({ label, value, sub }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-full shrink-0"
      style={{ width: 132, height: 132, border: `2px dashed ${C.gold}`, transform: "rotate(-4deg)", background: "rgba(184,147,95,0.06)" }}>
      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: C.gold, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontFamily: serif, fontSize: 34, fontWeight: 700, color: C.paper, lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: mono, fontSize: 10, color: C.goldLight }}>{sub}</div>
    </div>
  );
}
function SectionTitle({ eyebrow, title, right }) {
  return (
    <div className="flex items-end justify-between mb-4 pb-2 flex-wrap gap-2" style={{ borderBottom: `1px solid ${C.line}` }}>
      <div>
        <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 3, color: C.gold, textTransform: "uppercase" }}>{eyebrow}</div>
        <div style={{ fontFamily: serif, fontSize: 24, fontWeight: 700, color: C.ink }}>{title}</div>
      </div>
      {right}
    </div>
  );
}
function ProgressBar({ pct, color = C.sage }) {
  return (
    <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: C.paperDeep }}>
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, transition: "width .3s" }} />
    </div>
  );
}
function Card({ children, className = "" }) {
  return (
    <div className={`rounded-lg p-5 ${className}`} style={{ background: C.card, border: `1px solid ${C.line}`, boxShadow: "0 1px 2px rgba(27,42,74,0.06)" }}>
      {children}
    </div>
  );
}
function Btn({ children, onClick, variant = "primary", type = "button", small = false }) {
  const base = {
    fontFamily: sans, fontWeight: 600, fontSize: small ? 12 : 13,
    padding: small ? "6px 10px" : "9px 16px", borderRadius: 6,
    display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
    transition: "opacity .15s", border: "1px solid transparent",
  };
  const styles = {
    primary: { ...base, background: C.ink, color: C.paper },
    seal: { ...base, background: C.seal, color: "#fff" },
    ghost: { ...base, background: "transparent", color: C.ink, border: `1px solid ${C.line}` },
    danger: { ...base, background: "transparent", color: C.seal, border: `1px solid ${C.seal}55` },
  };
  return (
    <button type={type} onClick={onClick} style={styles[variant]}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.82)}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = 1)}>
      {children}
    </button>
  );
}
function Input(props) {
  return <input {...props} className={`w-full px-3 py-2 rounded outline-none ${props.className || ""}`}
    style={{ fontFamily: sans, fontSize: 13, background: "#fff", border: `1px solid ${C.line}`, color: C.charcoal, ...(props.style || {}) }} />;
}
function Select(props) {
  return <select {...props} className={`w-full px-3 py-2 rounded outline-none ${props.className || ""}`}
    style={{ fontFamily: sans, fontSize: 13, background: "#fff", border: `1px solid ${C.line}`, color: C.charcoal, ...(props.style || {}) }}>
    {props.children}
  </select>;
}
function TextArea(props) {
  return <textarea {...props} className={`w-full px-3 py-2 rounded outline-none ${props.className || ""}`}
    style={{ fontFamily: sans, fontSize: 13, background: "#fff", border: `1px solid ${C.line}`, color: C.charcoal, resize: "vertical", ...(props.style || {}) }} />;
}
function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 1, color: C.faint, textTransform: "uppercase" }}>{label}</span>
      {children}
    </div>
  );
}
function Check({ checked, onChange, label }) {
  return (
    <button type="button" onClick={onChange} className="flex items-center gap-1.5" style={{ fontFamily: sans, fontSize: 12.5, color: checked ? C.sage : C.faint }}>
      {checked ? <CheckCircle2 size={16} /> : <Circle size={16} />}
      {label}
    </button>
  );
}

/* ---------------------------------------------------------------------- */
/*  SUBJECTS TAB                                                          */
/* ---------------------------------------------------------------------- */
function SubjectCard({ subj, dailyLogs, update, open, onToggleOpen }) {
  const [chapterName, setChapterName] = useState("");
  const [bookForm, setBookForm] = useState({ title: "", author: "", notes: "" });
  const [openChapter, setOpenChapter] = useState(null);

  const patchSubject = (patch) => update((s) => ({ ...s, subjects: s.subjects.map((x) => (x.id === subj.id ? { ...x, ...patch } : x)) }));

  const addChapter = () => {
    if (!chapterName.trim()) return;
    const ch = { id: uid(), name: chapterName.trim(), order: subj.chapters.length + 1, stages: emptyStages() };
    patchSubject({ chapters: [...subj.chapters, ch] });
    setChapterName("");
  };
  const removeChapter = (id) => patchSubject({ chapters: subj.chapters.filter((c) => c.id !== id) });
  const toggleStage = (chapterId, key) => {
    patchSubject({
      chapters: subj.chapters.map((c) => {
        if (c.id !== chapterId) return c;
        const currently = c.stages[key].done;
        return { ...c, stages: { ...c.stages, [key]: { done: !currently, date: !currently ? todayISO() : "" } } };
      }),
    });
  };
  const setStageDate = (chapterId, key, date) => {
    patchSubject({ chapters: subj.chapters.map((c) => (c.id === chapterId ? { ...c, stages: { ...c.stages, [key]: { ...c.stages[key], date } } } : c)) });
  };
  const setDueDate = (key, date) => patchSubject({ dueDates: { ...subj.dueDates, [key]: date } });

  const addBook = () => {
    if (!bookForm.title.trim()) return;
    patchSubject({ books: [...subj.books, { id: uid(), ...bookForm }] });
    setBookForm({ title: "", author: "", notes: "" });
  };
  const removeBook = (id) => patchSubject({ books: subj.books.filter((b) => b.id !== id) });

  const pct = subjectProgress(subj);
  const hrs = subjectHours(subj, dailyLogs);

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
      const d = new Date(dateValue);
      if (isNaN(d)) return '';
      return d.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 cursor-pointer" onClick={onToggleOpen}>
        <div className="flex items-center gap-3">
          <span style={{ fontFamily: mono, fontSize: 11, color: C.gold }}>#{String(subj.order).padStart(2, "0")}</span>
          <span style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, color: C.ink }}>{subj.name}</span>
        </div>
        <div className="flex items-center gap-4">
          <span style={{ fontFamily: mono, fontSize: 11.5, color: C.gold }}>{hrs.toFixed(1)}h logged</span>
          <span style={{ fontFamily: mono, fontSize: 11.5, color: C.faint }}>{subj.chapters.length} chapters</span>
          {open ? <ChevronUp size={16} color={C.faint} /> : <ChevronDown size={16} color={C.faint} />}
        </div>
      </div>
      <div className="mt-3"><ProgressBar pct={pct} /></div>

      {open && (
        <div className="mt-5 flex flex-col gap-6">
          <div>
            <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 1, color: C.faint, textTransform: "uppercase", marginBottom: 8 }}>Due dates per stage</div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {STAGE_KEYS.map((k) => {
                const p = subjectStagePct(subj, k);
                const dueDate = subj.dueDates[k];
                const over = isOverdue(dueDate, p);
                const formattedDate = formatDateForInput(dueDate);
                
                return (
                  <div key={k}>
                    <div style={{ fontFamily: mono, fontSize: 10, color: over ? C.seal : C.faint }}>
                      {STAGE_LABELS[k]} {over ? "⚠" : ""}
                    </div>
                    <input 
                      type="date" 
                      value={formattedDate}
                      onChange={(e) => setDueDate(k, e.target.value)}
                      className="w-full px-2 py-1 rounded outline-none mt-1"
                      style={{ 
                        fontFamily: mono, 
                        fontSize: 11, 
                        background: dueDate ? "#fff" : "#f8f8f8", 
                        border: `1px solid ${over ? C.seal : C.line}`, 
                        color: dueDate ? C.charcoal : C.faint
                      }}
                    />
                    {dueDate && (
                      <div style={{ 
                        fontFamily: mono, 
                        fontSize: 9, 
                        color: C.sage, 
                        marginTop: 2,
                        textAlign: 'center'
                      }}>
                        {fmtDate(dueDate)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 1, color: C.faint, textTransform: "uppercase", marginBottom: 8 }}>Books / sources in use</div>
            <div className="flex flex-col gap-2 mb-2">
              {subj.books.map((b) => (
                <div key={b.id} className="flex items-center gap-3 flex-wrap" style={{ fontFamily: sans, fontSize: 12.5 }}>
                  <BookOpen size={13} color={C.gold} />
                  <span style={{ color: C.charcoal, fontWeight: 600 }}>{b.title}</span>
                  {b.author && <span style={{ color: C.faint }}>— {b.author}</span>}
                  {b.notes && <span style={{ color: C.faint }}>({b.notes})</span>}
                  <button onClick={() => removeBook(b.id)} style={{ color: C.seal, marginLeft: "auto" }}><Trash2 size={13} /></button>
                </div>
              ))}
              {subj.books.length === 0 && <div style={{ fontFamily: sans, fontSize: 12, color: C.faint }}>No books added yet.</div>}
            </div>
            <div className="grid md:grid-cols-4 gap-2">
              <Input placeholder="Book title" value={bookForm.title} onChange={(e) => setBookForm({ ...bookForm, title: e.target.value })} />
              <Input placeholder="Author / source" value={bookForm.author} onChange={(e) => setBookForm({ ...bookForm, author: e.target.value })} />
              <Input placeholder="Notes (e.g. NCERT, Laxmikanth)" value={bookForm.notes} onChange={(e) => setBookForm({ ...bookForm, notes: e.target.value })} />
              <Btn onClick={addBook} small><Plus size={13} /> Add Book</Btn>
            </div>
          </div>

          <div>
            <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 1, color: C.faint, textTransform: "uppercase", marginBottom: 8 }}>Chapters</div>
            <div className="flex gap-2 mb-3">
              <Input placeholder="Add a chapter name..." value={chapterName} onChange={(e) => setChapterName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addChapter(); }} />
              <Btn onClick={addChapter} small><Plus size={13} /> Add Chapter</Btn>
            </div>
            <div className="flex flex-col gap-2">
              {subj.chapters.length === 0 && <div style={{ fontFamily: sans, fontSize: 12, color: C.faint }}>No chapters yet — add your first chapter above.</div>}
              {subj.chapters.map((c) => {
                const cHrs = chapterHours(c.id, dailyLogs);
                return (
                  <div key={c.id} className="pb-3" style={{ borderBottom: `1px solid ${C.line}` }}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span style={{ fontFamily: sans, fontSize: 13.5, color: C.charcoal, fontWeight: 600 }}>{c.name}</span>
                        <span style={{ fontFamily: mono, fontSize: 10.5, color: C.gold }}>{cHrs.toFixed(1)}h</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setOpenChapter(openChapter === c.id ? null : c.id)} style={{ fontFamily: mono, fontSize: 10.5, color: C.faint }}>
                          {openChapter === c.id ? "hide dates" : "stage dates"}
                        </button>
                        <button onClick={() => removeChapter(c.id)} style={{ color: C.seal }}><Trash2 size={13} /></button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap mt-1.5">
                      {STAGE_KEYS.map((k) => (
                        <Check key={k} checked={c.stages[k].done} onChange={() => toggleStage(c.id, k)} label={STAGE_LABELS[k]} />
                      ))}
                    </div>
                    <div className="mt-1.5"><ProgressBar pct={chapterProgress(c)} /></div>
                    {openChapter === c.id && (
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-2">
                        {STAGE_KEYS.map((k) => (
                          <div key={k}>
                            <div style={{ fontFamily: mono, fontSize: 9.5, color: C.faint }}>{STAGE_LABELS[k]} date</div>
                            <input type="date" value={c.stages[k].date} onChange={(e) => setStageDate(c.id, k, e.target.value)} disabled={!c.stages[k].done}
                              className="w-full px-2 py-1 rounded outline-none mt-1"
                              style={{ fontFamily: mono, fontSize: 10.5, background: c.stages[k].done ? "#fff" : C.paperDeep, border: `1px solid ${C.line}`, color: C.charcoal }} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function SubjectsTab({ state, update, type, eyebrow, title }) {
  const [addingSubject, setAddingSubject] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState("");
  const [openId, setOpenId] = useState(null);
  const subjects = state.subjects.filter((s) => s.type === type);

  const addSubject = () => {
    if (!newSubjectName.trim()) return;
    const s = { id: uid(), name: newSubjectName.trim(), type, paper: null, order: subjects.length + 1, dueDates: emptyDueDates(), books: [], chapters: [] };
    update((st) => ({ ...st, subjects: [...st.subjects, s] }));
    setNewSubjectName(""); setAddingSubject(false);
  };
  const removeSubject = (id) => {
    if (window.confirm("Remove this subject and all its chapters/books? This cannot be undone.")) {
      update((st) => ({ ...st, subjects: st.subjects.filter((s) => s.id !== id) }));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <SectionTitle eyebrow={eyebrow} title={title} right={
        <Btn variant="ghost" small onClick={() => setAddingSubject(!addingSubject)}><Plus size={13} /> Add Subject</Btn>
      } />
      {addingSubject && (
        <Card>
          <div className="flex gap-2">
            <Input placeholder="New subject name..." value={newSubjectName} onChange={(e) => setNewSubjectName(e.target.value)} />
            <Btn onClick={addSubject} small>Add</Btn>
          </div>
        </Card>
      )}
      {subjects.map((s) => (
        <div key={s.id} className="flex flex-col gap-1">
          <SubjectCard subj={s} dailyLogs={state.dailyLogs} update={update} open={openId === s.id} onToggleOpen={() => setOpenId(openId === s.id ? null : s.id)} />
          <button onClick={() => removeSubject(s.id)} className="self-end"
            style={{ color: C.faint, fontSize: 10.5, fontFamily: mono, display: "flex", alignItems: "center", gap: 4 }}>
            <Trash2 size={11} /> delete subject
          </button>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/*  DAILY LOG - UPDATED WITH AUTO STAGE PROGRESSION                      */
/* ---------------------------------------------------------------------- */
function DailyLog({ state, update }) {
  const subjectOptions = state.subjects;
  const blank = { date: todayISO(), subjectId: "", chapterId: "", category: "", hoursActual: "", mcqsSolved: "", caDone: false, notes: "" };
  const [form, setForm] = useState(blank);

  const selectedSubject = subjectOptions.find((s) => s.id === form.subjectId);
  const selectedChapter = selectedSubject?.chapters.find((c) => c.id === form.chapterId);

  const addLog = () => {
    if (!form.date || !form.hoursActual || (!form.subjectId && !form.category)) return;
    
    update((s) => {
      // First, add the daily log
      const newLog = { id: uid(), ...form, hoursActual: Number(form.hoursActual), mcqsSolved: Number(form.mcqsSolved) || 0 };
      const updatedLogs = [newLog, ...s.dailyLogs];
      
      // If this log is for a specific chapter, update the chapter's stages
      let updatedSubjects = s.subjects;
      if (form.chapterId && form.subjectId) {
        const hoursLogged = Number(form.hoursActual);
        // Only auto-update if at least 0.5 hours logged
        if (hoursLogged >= 0.5) {
          updatedSubjects = s.subjects.map((subj) => {
            if (subj.id === form.subjectId) {
              return {
                ...subj,
                chapters: subj.chapters.map((ch) => {
                  if (ch.id === form.chapterId) {
                    // Find the next incomplete stage
                    const nextStage = getNextIncompleteStage(ch);
                    
                    // If there's an incomplete stage and it's not already marked done
                    if (nextStage && !ch.stages[nextStage.key].done) {
                      return {
                        ...ch,
                        stages: {
                          ...ch.stages,
                          [nextStage.key]: {
                            done: true,
                            date: form.date || todayISO()
                          }
                        }
                      };
                    }
                  }
                  return ch;
                })
              };
            }
            return subj;
          });
        }
      }
      
      return {
        ...s,
        dailyLogs: updatedLogs,
        subjects: updatedSubjects
      };
    });
    
    setForm({ ...blank, subjectId: form.subjectId, category: form.category });
  };

  const removeLog = (id) => update((s) => ({ ...s, dailyLogs: s.dailyLogs.filter((l) => l.id !== id) }));

  const resolveLabel = (l) => {
    if (l.category) return l.category;
    const subj = subjectOptions.find((s) => s.id === l.subjectId);
    if (!subj) return "—";
    const ch = subj.chapters.find((c) => c.id === l.chapterId);
    return ch ? `${subj.name} — ${ch.name}` : subj.name;
  };

  const sorted = [...state.dailyLogs].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Get next stage info for the selected chapter
  const nextStageInfo = selectedChapter ? getNextIncompleteStage(selectedChapter) : null;
  const isChapterComplete = selectedChapter && !nextStageInfo;

  return (
    <div className="flex flex-col gap-5">
      <SectionTitle eyebrow="Daily Discipline" title="Day-wise & Chapter-wise Study Log" />
      
      {/* Show next stage info when a chapter is selected */}
      {selectedChapter && (
        <Card>
          <div style={{ fontFamily: sans, fontSize: 14, color: C.charcoal }}>
            <span style={{ fontWeight: 600 }}>Next milestone for {selectedChapter.name}:</span>
            <span style={{ 
              color: isChapterComplete ? C.green : C.gold,
              fontWeight: 600,
              marginLeft: 8
            }}>
              {isChapterComplete ? '✅ Complete! All stages done' : nextStageInfo?.label || 'Complete!'}
            </span>
          </div>
          <div className="mt-2">
            <ProgressBar pct={chapterProgress(selectedChapter)} />
          </div>
          {!isChapterComplete && nextStageInfo && (
            <div style={{ fontFamily: mono, fontSize: 10.5, color: C.faint, marginTop: 6 }}>
              Log at least 0.5 hours to automatically mark "{nextStageInfo.label}" as complete
            </div>
          )}
        </Card>
      )}

      <Card>
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Subject">
            <Select value={form.subjectId} onChange={(e) => setForm({ ...form, subjectId: e.target.value, chapterId: "", category: "" })}>
              <option value="">— Select subject —</option>
              {subjectOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
          <Field label="Chapter">
            <Select value={form.chapterId} onChange={(e) => setForm({ ...form, chapterId: e.target.value })} disabled={!selectedSubject}>
              <option value="">General / whole subject</option>
              {selectedSubject?.chapters.map((c) => {
                const pct = chapterProgress(c);
                return (
                  <option key={c.id} value={c.id}>
                    {c.name} {pct === 100 ? '✅' : `(${pct}%)`}
                  </option>
                );
              })}
            </Select>
          </Field>
        </div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span style={{ fontFamily: mono, fontSize: 10.5, color: C.faint }}>or log against:</span>
          {EXTRA_LOG_CATEGORIES.map((cat) => (
            <button key={cat} onClick={() => setForm({ ...form, category: cat, subjectId: "", chapterId: "" })}
              style={{
                fontFamily: mono, fontSize: 10.5, padding: "3px 9px", borderRadius: 999,
                border: `1px solid ${form.category === cat ? C.ink : C.line}`,
                background: form.category === cat ? C.ink : "transparent",
                color: form.category === cat ? C.paper : C.faint,
              }}>{cat}</button>
          ))}
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <Field label="Hours Studied"><Input type="number" min="0" step="0.5" value={form.hoursActual} onChange={(e) => setForm({ ...form, hoursActual: e.target.value })} placeholder="2.5" /></Field>
          <Field label="MCQs Solved"><Input type="number" min="0" value={form.mcqsSolved} onChange={(e) => setForm({ ...form, mcqsSolved: e.target.value })} placeholder="20" /></Field>
          <Field label="Current Affairs">
            <div className="pt-2"><Check checked={form.caDone} onChange={() => setForm({ ...form, caDone: !form.caDone })} label={form.caDone ? "Covered" : "Not covered"} /></div>
          </Field>
          <div className="flex items-end"><Btn onClick={addLog}><Plus size={14} /> Add Entry</Btn></div>
        </div>
        <div className="mt-3"><Field label="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What did you cover today?" /></Field></div>
      </Card>

      <Card>
        <div className="flex flex-col gap-2 max-h-[440px] overflow-auto pr-1">
          {sorted.length === 0 && <div style={{ fontFamily: sans, fontSize: 13, color: C.faint }}>No entries yet. Log today's study above.</div>}
          {sorted.map((l) => {
            const isChapterLog = !!l.chapterId;
            const subj = subjectOptions.find((s) => s.id === l.subjectId);
            const ch = subj?.chapters.find((c) => c.id === l.chapterId);
            
            // Find which stage was completed by this log (if any)
            let completedStage = null;
            if (isChapterLog && ch) {
              // Check all stages and see which one has the same date as this log
              for (const key of STAGE_KEYS) {
                if (ch.stages[key].date === l.date && ch.stages[key].done) {
                  completedStage = STAGE_LABELS[key];
                  break;
                }
              }
            }
            
            return (
              <div key={l.id} className="flex items-center gap-3 py-2 flex-wrap" style={{ borderBottom: `1px solid ${C.line}` }}>
                <span style={{ fontFamily: mono, fontSize: 11.5, color: C.gold, width: 90 }}>{fmtDate(l.date)}</span>
                <span style={{ fontFamily: sans, fontSize: 13, width: 220, color: C.charcoal }}>{resolveLabel(l)}</span>
                <span style={{ fontFamily: mono, fontSize: 12, color: C.sage, width: 55 }}>{l.hoursActual}h</span>
                <span style={{ fontFamily: mono, fontSize: 12, color: C.faint, width: 85 }}>{l.mcqsSolved} MCQs</span>
                <span style={{ fontFamily: sans, fontSize: 11, color: l.caDone ? C.sage : C.faint, width: 70 }}>{l.caDone ? "CA ✓" : "CA –"}</span>
                {completedStage && (
                  <span style={{ fontFamily: mono, fontSize: 10, color: C.gold, width: 80 }}>
                    → {completedStage} ✓
                  </span>
                )}
                <span style={{ fontFamily: sans, fontSize: 12, color: C.faint, flex: 1 }}>{l.notes}</span>
                <button onClick={() => removeLog(l.id)} style={{ color: C.seal }}><Trash2 size={14} /></button>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/*  CURRENT AFFAIRS                                                        */
/* ---------------------------------------------------------------------- */
function CurrentAffairsTab({ state, update }) {
  const blank = { date: todayISO(), folder: CA_FOLDERS[0], whyInNews: "", prelimsFacts: "", staticLink: "", mainsRelevance: "" };
  const [form, setForm] = useState(blank);
  const [filter, setFilter] = useState("All");

  const addEntry = () => {
    if (!form.whyInNews) return;
    update((s) => ({ ...s, currentAffairs: [{ id: uid(), ...form }, ...s.currentAffairs] }));
    setForm({ ...blank, folder: form.folder });
  };
  const removeEntry = (id) => update((s) => ({ ...s, currentAffairs: s.currentAffairs.filter((c) => c.id !== id) }));
  const filtered = state.currentAffairs.filter((c) => filter === "All" || c.folder === filter);

  return (
    <div className="flex flex-col gap-5">
      <SectionTitle eyebrow="8 Folders · Vision IAS Monthly" title="Current Affairs Register" />
      <Card>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Folder">
            <Select value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })}>
              {CA_FOLDERS.map((f) => <option key={f} value={f}>{f}</option>)}
            </Select>
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <Field label="Why in News?"><TextArea rows={2} value={form.whyInNews} onChange={(e) => setForm({ ...form, whyInNews: e.target.value })} /></Field>
          <Field label="Prelims Facts"><TextArea rows={2} value={form.prelimsFacts} onChange={(e) => setForm({ ...form, prelimsFacts: e.target.value })} /></Field>
          <Field label="Static Chapter Link"><Input value={form.staticLink} onChange={(e) => setForm({ ...form, staticLink: e.target.value })} placeholder="e.g. Polity — Art 356" /></Field>
          <Field label="Mains Relevance"><TextArea rows={2} value={form.mainsRelevance} onChange={(e) => setForm({ ...form, mainsRelevance: e.target.value })} /></Field>
        </div>
        <div className="mt-3"><Btn onClick={addEntry}><Plus size={14} /> Add Note</Btn></div>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        {["All", ...CA_FOLDERS].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            style={{
              fontFamily: mono, fontSize: 11, padding: "4px 10px", borderRadius: 999,
              border: `1px solid ${filter === f ? C.ink : C.line}`,
              background: filter === f ? C.ink : "transparent",
              color: filter === f ? C.paper : C.faint,
            }}>{f}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {filtered.length === 0 && <div style={{ fontFamily: sans, fontSize: 13, color: C.faint }}>No notes in this folder yet.</div>}
        {filtered.map((c) => (
          <Card key={c.id}>
            <div className="flex items-center justify-between mb-2">
              <span style={{ fontFamily: mono, fontSize: 10.5, color: C.gold, textTransform: "uppercase" }}>{c.folder}</span>
              <div className="flex items-center gap-2">
                <span style={{ fontFamily: mono, fontSize: 11, color: C.faint }}>{fmtDate(c.date)}</span>
                <button onClick={() => removeEntry(c.id)} style={{ color: C.seal }}><Trash2 size={13} /></button>
              </div>
            </div>
            <div style={{ fontFamily: serif, fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 4 }}>{c.whyInNews}</div>
            {c.prelimsFacts && <div style={{ fontFamily: sans, fontSize: 12.5, color: C.charcoal, marginBottom: 3 }}><b>Prelims:</b> {c.prelimsFacts}</div>}
            {c.staticLink && <div style={{ fontFamily: sans, fontSize: 12.5, color: C.sage, marginBottom: 3 }}><b>Link:</b> {c.staticLink}</div>}
            {c.mainsRelevance && <div style={{ fontFamily: sans, fontSize: 12.5, color: C.charcoal }}><b>Mains:</b> {c.mainsRelevance}</div>}
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/*  ESSAY & MOCK TESTS                                                     */
/* ---------------------------------------------------------------------- */
function EssayMocks({ state, update }) {
  const blankEssay = { date: todayISO(), topic: "", wordCount: "", completed: false, feedback: "" };
  const [essayForm, setEssayForm] = useState(blankEssay);

  const blankMock = { date: todayISO(), source: "", scope: "Full Syllabus", subjectId: "", chapterId: "", score: "", maxScore: 200, notes: "" };
  const [mockForm, setMockForm] = useState(blankMock);
  const mockSubject = state.subjects.find((s) => s.id === mockForm.subjectId);

  const addEssay = () => {
    if (!essayForm.topic) return;
    update((s) => ({ ...s, essays: [{ id: uid(), ...essayForm, wordCount: Number(essayForm.wordCount) || 0 }, ...s.essays] }));
    setEssayForm(blankEssay);
  };
  const toggleEssay = (id) => update((s) => ({ ...s, essays: s.essays.map((e) => (e.id === id ? { ...e, completed: !e.completed } : e)) }));
  const removeEssay = (id) => update((s) => ({ ...s, essays: s.essays.filter((e) => e.id !== id) }));

  const addMock = () => {
    if (!mockForm.score || !mockForm.source) return;
    update((s) => ({ ...s, mockTests: [{ id: uid(), ...mockForm, score: Number(mockForm.score), maxScore: Number(mockForm.maxScore) || 200 }, ...s.mockTests] }));
    setMockForm({ ...blankMock, source: mockForm.source, scope: mockForm.scope });
  };
  const removeMock = (id) => update((s) => ({ ...s, mockTests: s.mockTests.filter((m) => m.id !== id) }));

  const resolveMockScope = (m) => {
    if (m.scope === "Full Syllabus") return "Full Syllabus";
    const subj = state.subjects.find((s) => s.id === m.subjectId);
    if (!subj) return m.scope;
    if (m.scope === "Chapter Wise") {
      const ch = subj.chapters.find((c) => c.id === m.chapterId);
      return `${subj.name} — ${ch ? ch.name : "Chapter"}`;
    }
    return subj.name;
  };

  const chartData = useMemo(() =>
    [...state.mockTests].sort((a, b) => new Date(a.date) - new Date(b.date)).map((m) => ({
      date: fmtDate(m.date), pct: Math.round((m.score / m.maxScore) * 100),
    })), [state.mockTests]);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <SectionTitle eyebrow="1 Essay / Week from Jan 2027" title="Essay Practice" />
        <Card>
          <div className="grid md:grid-cols-4 gap-3">
            <Field label="Date"><Input type="date" value={essayForm.date} onChange={(e) => setEssayForm({ ...essayForm, date: e.target.value })} /></Field>
            <Field label="Topic"><Input value={essayForm.topic} onChange={(e) => setEssayForm({ ...essayForm, topic: e.target.value })} placeholder="Essay topic" /></Field>
            <Field label="Word Count"><Input type="number" value={essayForm.wordCount} onChange={(e) => setEssayForm({ ...essayForm, wordCount: e.target.value })} placeholder="1000" /></Field>
            <div className="flex items-end"><Btn onClick={addEssay}><Plus size={14} /> Add Essay</Btn></div>
          </div>
          <div className="mt-3"><Field label="Feedback / Self-review"><Input value={essayForm.feedback} onChange={(e) => setEssayForm({ ...essayForm, feedback: e.target.value })} /></Field></div>
        </Card>
        <div className="flex flex-col gap-2 mt-3">
          {state.essays.map((e) => (
            <div key={e.id} className="flex items-center gap-3 py-2 flex-wrap" style={{ borderBottom: `1px solid ${C.line}` }}>
              <button onClick={() => toggleEssay(e.id)} style={{ color: e.completed ? C.sage : C.faint }}>
                {e.completed ? <CheckCircle2 size={16} /> : <Circle size={16} />}
              </button>
              <span style={{ fontFamily: mono, fontSize: 11, color: C.gold, width: 90 }}>{fmtDate(e.date)}</span>
              <span style={{ fontFamily: sans, fontSize: 13, flex: 1, color: C.charcoal }}>{e.topic}</span>
              <span style={{ fontFamily: mono, fontSize: 11, color: C.faint }}>{e.wordCount}w</span>
              <button onClick={() => removeEssay(e.id)} style={{ color: C.seal }}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionTitle eyebrow="Target: 20–30 Full-Length" title="Mock Test Scores" />
        <Card>
          <div className="grid md:grid-cols-3 gap-3 mb-3">
            <Field label="Date"><Input type="date" value={mockForm.date} onChange={(e) => setMockForm({ ...mockForm, date: e.target.value })} /></Field>
            <Field label="Test Series / PDF Source"><Input value={mockForm.source} onChange={(e) => setMockForm({ ...mockForm, source: e.target.value })} placeholder="e.g. Vision IAS Prelims Test 12, or a PDF name" /></Field>
            <Field label="Scope">
              <Select value={mockForm.scope} onChange={(e) => setMockForm({ ...mockForm, scope: e.target.value, subjectId: "", chapterId: "" })}>
                <option>Full Syllabus</option>
                <option>Subject Wise</option>
                <option>Chapter Wise</option>
              </Select>
            </Field>
          </div>
          {mockForm.scope !== "Full Syllabus" && (
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <Field label="Subject">
                <Select value={mockForm.subjectId} onChange={(e) => setMockForm({ ...mockForm, subjectId: e.target.value, chapterId: "" })}>
                  <option value="">— Select subject —</option>
                  {state.subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Select>
              </Field>
              {mockForm.scope === "Chapter Wise" && (
                <Field label="Chapter">
                  <Select value={mockForm.chapterId} onChange={(e) => setMockForm({ ...mockForm, chapterId: e.target.value })} disabled={!mockSubject}>
                    <option value="">— Select chapter —</option>
                    {mockSubject?.chapters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </Field>
              )}
            </div>
          )}
          <div className="grid md:grid-cols-4 gap-3">
            <Field label="Score"><Input type="number" value={mockForm.score} onChange={(e) => setMockForm({ ...mockForm, score: e.target.value })} placeholder="140" /></Field>
            <Field label="Max Score"><Input type="number" value={mockForm.maxScore} onChange={(e) => setMockForm({ ...mockForm, maxScore: e.target.value })} /></Field>
            <Field label="Notes"><Input value={mockForm.notes} onChange={(e) => setMockForm({ ...mockForm, notes: e.target.value })} placeholder="Weak areas..." /></Field>
            <div className="flex items-end"><Btn onClick={addMock}><Plus size={14} /> Add Score</Btn></div>
          </div>
        </Card>

        {chartData.length > 1 && (
          <Card className="mt-3">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid stroke={C.line} strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontFamily: mono, fontSize: 10, fill: C.faint }} />
                <YAxis tick={{ fontFamily: mono, fontSize: 10, fill: C.faint }} unit="%" />
                <Tooltip contentStyle={{ fontFamily: sans, fontSize: 12, borderColor: C.line }} />
                <Line type="monotone" dataKey="pct" stroke={C.seal} strokeWidth={2} dot={{ fill: C.seal, r: 3 }} name="Score %" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        <div className="flex flex-col gap-2 mt-3">
          {state.mockTests.map((m) => (
            <div key={m.id} className="flex items-center gap-3 py-2 flex-wrap" style={{ borderBottom: `1px solid ${C.line}` }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: C.gold, width: 90 }}>{fmtDate(m.date)}</span>
              <span style={{ fontFamily: sans, fontSize: 12.5, width: 160, color: C.charcoal }}>{m.source}</span>
              <span style={{ fontFamily: mono, fontSize: 10.5, color: C.faint, width: 190 }}>{resolveMockScope(m)}</span>
              <span style={{ fontFamily: mono, fontSize: 12, color: C.sage }}>{m.score}/{m.maxScore} ({Math.round((m.score / m.maxScore) * 100)}%)</span>
              <span style={{ fontFamily: sans, fontSize: 12, color: C.faint, flex: 1 }}>{m.notes}</span>
              <button onClick={() => removeMock(m.id)} style={{ color: C.seal }}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/*  DATA MANAGER                                                          */
/* ---------------------------------------------------------------------- */
function DataManager({ state, setState, lastSynced, syncStatus, syncError, onSyncNow }) {
  const fileRef = useRef(null);
  const [msg, setMsg] = useState("");
  const boolify = (v) => v === true || v === "TRUE" || v === "true" || v === 1 || v === "1";

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    const subjectsRows = state.subjects.map((s) => ({
      id: s.id, name: s.name, type: s.type, paper: s.paper || "", order: s.order,
      due_read1: s.dueDates.read1, due_rev1: s.dueDates.rev1, due_rev2: s.dueDates.rev2,
      due_rev3: s.dueDates.rev3, due_mcq: s.dueDates.mcq, due_pyq: s.dueDates.pyq,
    }));
    const chaptersRows = state.subjects.flatMap((s) => s.chapters.map((c) => ({
      id: c.id, subjectId: s.id, subjectName: s.name, name: c.name, order: c.order,
      read1_done: c.stages.read1.done, read1_date: c.stages.read1.date,
      rev1_done: c.stages.rev1.done, rev1_date: c.stages.rev1.date,
      rev2_done: c.stages.rev2.done, rev2_date: c.stages.rev2.date,
      rev3_done: c.stages.rev3.done, rev3_date: c.stages.rev3.date,
      mcq_done: c.stages.mcq.done, mcq_date: c.stages.mcq.date,
      pyq_done: c.stages.pyq.done, pyq_date: c.stages.pyq.date,
    })));
    const booksRows = state.subjects.flatMap((s) => s.books.map((b) => ({
      id: b.id, subjectId: s.id, subjectName: s.name, title: b.title, author: b.author, notes: b.notes,
    })));
    const dailyLogRows = state.dailyLogs.map((l) => {
      const subj = state.subjects.find((s) => s.id === l.subjectId);
      const ch = subj?.chapters.find((c) => c.id === l.chapterId);
      return { id: l.id, date: l.date, subjectId: l.subjectId, subjectName: subj?.name || "", chapterId: l.chapterId, chapterName: ch?.name || "", category: l.category, hoursActual: l.hoursActual, mcqsSolved: l.mcqsSolved, caDone: l.caDone, notes: l.notes };
    });
    const mockRows = state.mockTests.map((m) => {
      const subj = state.subjects.find((s) => s.id === m.subjectId);
      const ch = subj?.chapters.find((c) => c.id === m.chapterId);
      return { id: m.id, date: m.date, source: m.source, scope: m.scope, subjectId: m.subjectId, subjectName: subj?.name || "", chapterId: m.chapterId, chapterName: ch?.name || "", score: m.score, maxScore: m.maxScore, notes: m.notes };
    });

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(subjectsRows), "Subjects");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(chaptersRows), "Chapters");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(booksRows), "Books");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dailyLogRows), "Daily_Log");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(state.currentAffairs), "Current_Affairs");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(state.essays), "Essays");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(mockRows), "Mock_Tests");
    XLSX.writeFile(wb, `upsc-tracker-${todayISO()}.xlsx`);
    setMsg("Exported successfully.");
  };

  const importExcel = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "array" });
        const sheet = (name) => (wb.Sheets[name] ? XLSX.utils.sheet_to_json(wb.Sheets[name]) : null);

        const subjectsRows = sheet("Subjects");
        const chaptersRows = sheet("Chapters") || [];
        const booksRows = sheet("Books") || [];
        const dailyLogRows = sheet("Daily_Log");
        const currentAffairs = sheet("Current_Affairs");
        const essays = sheet("Essays");
        const mockRows = sheet("Mock_Tests");

        setState((prev) => {
          let subjects = prev.subjects;
          if (subjectsRows) {
            subjects = subjectsRows.map((s) => ({
              id: s.id, name: s.name, type: s.type, paper: s.paper || null, order: Number(s.order) || 1,
              dueDates: { read1: s.due_read1 || "", rev1: s.due_rev1 || "", rev2: s.due_rev2 || "", rev3: s.due_rev3 || "", mcq: s.due_mcq || "", pyq: s.due_pyq || "" },
              books: booksRows.filter((b) => b.subjectId === s.id).map((b) => ({ id: b.id || uid(), title: b.title, author: b.author, notes: b.notes })),
              chapters: chaptersRows.filter((c) => c.subjectId === s.id).map((c) => ({
                id: c.id || uid(), name: c.name, order: Number(c.order) || 1,
                stages: {
                  read1: { done: boolify(c.read1_done), date: c.read1_date || "" },
                  rev1: { done: boolify(c.rev1_done), date: c.rev1_date || "" },
                  rev2: { done: boolify(c.rev2_done), date: c.rev2_date || "" },
                  rev3: { done: boolify(c.rev3_done), date: c.rev3_date || "" },
                  mcq: { done: boolify(c.mcq_done), date: c.mcq_date || "" },
                  pyq: { done: boolify(c.pyq_done), date: c.pyq_date || "" },
                },
              })),
            }));
          }
          return {
            subjects,
            dailyLogs: dailyLogRows ? dailyLogRows.map((l) => ({ id: l.id || uid(), date: l.date, subjectId: l.subjectId || "", chapterId: l.chapterId || "", category: l.category || "", hoursActual: Number(l.hoursActual) || 0, mcqsSolved: Number(l.mcqsSolved) || 0, caDone: boolify(l.caDone), notes: l.notes || "" })) : prev.dailyLogs,
            currentAffairs: currentAffairs ? currentAffairs.map((c) => ({ ...c, id: c.id || uid() })) : prev.currentAffairs,
            essays: essays ? essays.map((e) => ({ ...e, id: e.id || uid(), completed: boolify(e.completed) })) : prev.essays,
            mockTests: mockRows ? mockRows.map((m) => ({ id: m.id || uid(), date: m.date, source: m.source || "", scope: m.scope || "Full Syllabus", subjectId: m.subjectId || "", chapterId: m.chapterId || "", score: Number(m.score) || 0, maxScore: Number(m.maxScore) || 200, notes: m.notes || "" })) : prev.mockTests,
          };
        });
        setMsg("Imported successfully. Your tracker has been updated.");
      } catch (err) {
        setMsg("Import failed — please check the file format.");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const resetAll = () => {
    if (window.confirm("This will erase all logged progress and reset to defaults. Continue?")) {
      setState(DEFAULT_STATE);
      setMsg("All data reset to defaults.");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <SectionTitle eyebrow="Records Office" title="Data — Google Sheet & Excel" />

      <Card>
        <div className="flex items-center gap-2 mb-2">
          <span style={{
            width: 8, height: 8, borderRadius: 999,
            background: syncStatus === "saved" ? C.sage : syncStatus === "error" ? C.seal : C.gold,
          }} />
          <span style={{ fontFamily: mono, fontSize: 12, color: C.charcoal, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {syncStatus === "saving" && "Syncing with Google Sheet…"}
            {syncStatus === "saved" && "Connected — synced with your Google Sheet"}
            {syncStatus === "error" && "Sync error — working from local cache"}
            {syncStatus === "idle" && "Not yet synced"}
          </span>
        </div>
        <div style={{ fontFamily: sans, fontSize: 13, color: C.charcoal, lineHeight: 1.6 }}>
          This tracker stores everything (subjects, chapters, books, daily logs, current affairs,
          essays, mock tests) as one record in your Google Sheet, via your Apps Script web app:
        </div>
        <div style={{ fontFamily: mono, fontSize: 10.5, color: C.faint, wordBreak: "break-all", marginTop: 6 }}>{SHEET_API_URL}</div>
        {syncError && (
          <div style={{ fontFamily: sans, fontSize: 12.5, color: C.seal, marginTop: 10 }}>
            {syncError} — check that the Apps Script is deployed with access set to "Anyone", and that it
            has <code>doGet</code>/<code>doPost</code> handlers (see the Code.gs file provided alongside this app).
          </div>
        )}
        <div className="flex flex-wrap gap-3 mt-4">
          <Btn onClick={onSyncNow} variant="primary"><Save size={14} /> Sync Now</Btn>
        </div>
        <div style={{ fontFamily: mono, fontSize: 11, color: C.faint, marginTop: 12 }}>
          {lastSynced ? `Last synced at ${lastSynced}` : "Not yet synced"}
        </div>
      </Card>

      <Card>
        <div style={{ fontFamily: sans, fontSize: 13, color: C.charcoal, marginBottom: 16, lineHeight: 1.6 }}>
          You can still export a readable, multi-tab Excel workbook any time (Subjects, Chapters, Books,
          Daily Log, Current Affairs, Essays, Mock Tests — seven sheets), or import one to restore/update your tracker.
        </div>
        <div className="flex flex-wrap gap-3">
          <Btn onClick={exportExcel} variant="ghost"><Download size={14} /> Export to Excel</Btn>
          <Btn onClick={() => fileRef.current?.click()} variant="ghost"><Upload size={14} /> Import from Excel</Btn>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={importExcel} className="hidden" />
          <Btn onClick={resetAll} variant="danger"><RotateCcw size={14} /> Reset All Data</Btn>
        </div>
        {msg && <div style={{ fontFamily: mono, fontSize: 12, color: C.sage, marginTop: 12 }}>{msg}</div>}
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/*  APP SHELL                                                              */
/* ---------------------------------------------------------------------- */
const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "gs", label: "GS Subjects", icon: BookOpen },
  { key: "optional", label: "Optional (Maths)", icon: Calculator },
  { key: "daily", label: "Daily Log", icon: Clock },
  { key: "ca", label: "Current Affairs", icon: Newspaper },
  { key: "essay", label: "Essay & Mocks", icon: PenSquare },
  { key: "data", label: "Data", icon: Database },
];

export default function App() {
  const [state, setState] = useState(DEFAULT_STATE);
  const [tab, setTab] = useState("dashboard");
  const [loaded, setLoaded] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [syncStatus, setSyncStatus] = useState("idle");
  const [syncError, setSyncError] = useState("");
  const saveTimeout = useRef(null);
  const skipNextSave = useRef(true);

  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Inter:wght@400;500;600&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch (e) {} };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const sheetData = await loadFromSheet();
        if (sheetData && Object.keys(sheetData).length) {
          setState({ ...DEFAULT_STATE, ...sheetData });
        }
        setSyncStatus("saved");
        setLastSynced(new Date().toLocaleTimeString("en-IN"));
      } catch (e) {
        setSyncStatus("error");
        setSyncError(e.message || "Could not reach the Google Sheet.");
      } finally {
        skipNextSave.current = true;
        setLoaded(true);
      }
    })();
  }, []);

  const update = (fn) => setState((prev) => fn(prev));

  useEffect(() => {
    if (!loaded) return;
    if (skipNextSave.current) { skipNextSave.current = false; return; }
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      setSyncStatus("saving");
      try {
        await saveToSheet(state);
        setSyncStatus("saved");
        setSyncError("");
        setLastSynced(new Date().toLocaleTimeString("en-IN"));
      } catch (e) {
        setSyncStatus("error");
        setSyncError(e.message || "Could not reach the Google Sheet.");
      }
    }, 800);
    return () => clearTimeout(saveTimeout.current);
  }, [state, loaded]);

  const syncNow = async () => {
    setSyncStatus("saving");
    try {
      await saveToSheet(state);
      setSyncStatus("saved");
      setSyncError("");
      setLastSynced(new Date().toLocaleTimeString("en-IN"));
    } catch (e) {
      setSyncStatus("error");
      setSyncError(e.message || "Could not reach the Google Sheet.");
    }
  };

  if (!loaded) {
    return (
      <div className="w-full h-full min-h-[400px] flex items-center justify-center" style={{ background: C.paper, fontFamily: mono, color: C.faint }}>
        Opening ledger…
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen" style={{ background: C.paper, fontFamily: sans }}>
      <div style={{ background: C.inkDeep, borderBottom: `3px double ${C.gold}` }} className="px-6 py-5">
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div>
            <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 3, color: C.gold, textTransform: "uppercase" }}>
              Union Public Service Commission · Civil Services Examination
            </div>
            <div style={{ fontFamily: serif, fontSize: 26, fontWeight: 700, color: C.paper }}>CSE 2027 Preparation Ledger</div>
          </div>
          <div className="flex items-center gap-3">
            <div style={{ fontFamily: mono, fontSize: 11, color: C.goldLight, textAlign: "right" }}>Optional: Mathematics<br />Prelims target: 120+</div>
            <span title={syncError || ""} style={{
              fontFamily: mono, fontSize: 10, padding: "3px 8px", borderRadius: 999,
              border: `1px solid ${syncStatus === "error" ? C.seal : C.gold}`,
              color: syncStatus === "error" ? C.seal : C.goldLight,
            }}>
              {syncStatus === "saving" && "syncing…"}
              {syncStatus === "saved" && "synced ✓"}
              {syncStatus === "error" && "sync error"}
              {syncStatus === "idle" && "—"}
            </span>
          </div>
        </div>
      </div>

      <div style={{ background: C.ink }} className="px-6">
        <div className="max-w-6xl mx-auto flex gap-1 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} className="flex items-center gap-2 px-4 py-3 whitespace-nowrap"
              style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: tab === key ? C.paper : "#8B96AE", borderBottom: `2px solid ${tab === key ? C.gold : "transparent"}` }}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {tab === "dashboard" && <LeetCodeDashboard state={state} />}
        {tab === "gs" && <SubjectsTab state={state} update={update} type="GS" eyebrow="Static Syllabus" title="GS Subjects — Chapters, Books & Due Dates" />}
        {tab === "optional" && <SubjectsTab state={state} update={update} type="Optional" eyebrow="Optional Subject" title="Mathematics — Chapters, Books & Due Dates" />}
        {tab === "daily" && <DailyLog state={state} update={update} />}
        {tab === "ca" && <CurrentAffairsTab state={state} update={update} />}
        {tab === "essay" && <EssayMocks state={state} update={update} />}
        {tab === "data" && <DataManager state={state} setState={setState} lastSynced={lastSynced} syncStatus={syncStatus} syncError={syncError} onSyncNow={syncNow} />}
      </div>

      <div className="text-center py-6" style={{ fontFamily: mono, fontSize: 10.5, color: C.faint }}>
        Data is stored in your Google Sheet and persists across sessions. Export regularly to Excel for a personal backup.
      </div>
    </div>
  );
}